// ServicesScreen — sell your time and skill. Set an hourly rate, choose how many
// hours to work this week (capped by energy), and collect the revenue at the next
// weekly settle. High margin, but the hours are the ceiling — that's the S-quadrant
// lesson: when YOU are the product, you can only sell so many hours of yourself.
//
// Same supply/demand + energy systems as the business. Income lands in the week's
// cashflow → flows into the Payday harvest like any other profit.

import { useState } from 'react';
import {
  serviceWeeklyResult,
  serviceDemandHours,
  maxServiceHours,
  SERVICE_SWEET_RATE,
  ENERGY_PER_HOUR,
} from '@biz-kid/engine';
import { useDispatch, useGameState, selectHappiness, selectServices } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, Money, money, Bar } from '../../ui/kit.js';
import { CartoonServicesScene as ServicesScene } from '../../ui/cartoon.js';

export function ServicesScreen({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const services = useGameState(selectServices);
  const energy = useGameState(selectHappiness);

  // Local editing state seeds from the persisted venture so a reload keeps choices.
  const [rate, setRate] = useState(services.hourlyRate || SERVICE_SWEET_RATE);
  const [hours, setHours] = useState(services.plannedHours || 0);

  const maxHours = maxServiceHours(energy);
  const demandHours = serviceDemandHours(rate);
  const result = serviceWeeklyResult({ rate, plannedHours: hours, energy });
  const hourCap = Math.min(maxHours, 40);

  const start = () => dispatch({ kind: 'StartServices' });
  const commitRate = (r: number) => {
    setRate(r);
    dispatch({ kind: 'SetServiceRate', rate: r });
  };
  const commitHours = (h: number) => {
    const clamped = Math.min(h, maxHours);
    setHours(clamped);
    dispatch({ kind: 'SetServiceHours', hours: clamped });
  };

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 7, overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">AI agency · high margin, scales</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>🤖 AI Agency</h1>
          </div>
          <Button tone="ghost" size="sm" onClick={onClose}>← Home</Button>
        </header>

        {/* Voxel atmosphere — Miko at the workbench (time + tools). */}
        <Card pad={0} style={{ overflow: 'hidden', marginBottom: 14 }}>
          <ServicesScene />
        </Card>

        {!services.active ? (
          <Card>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>
              Sell AI-made logos, scripts and content to businesses — high margin, but your hours are the limit.
            </p>
            <div style={{ marginTop: 16 }}>
              <Button tone="primary" size="lg" onClick={start}>Take your first client →</Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Rate */}
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <Eyebrow>Your hourly rate</Eyebrow>
                <span className="bk-tnum" style={{ fontSize: 20, fontWeight: 900, color: 'var(--text)' }}>{money(rate)}/h</span>
              </div>
              <input
                type="range"
                min={2}
                max={40}
                step={1}
                value={rate}
                onChange={(e) => commitRate(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--brand)' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>B$2 (cheap)</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>B$40 (premium)</span>
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                💡 Charge more and you earn more per hour — but <b>fewer clients want you</b>. At your
                rate, clients want <b style={{ color: 'var(--text)' }}>{demandHours}h</b> of work this week.
              </p>
            </Card>

            {/* Hours */}
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <Eyebrow>Hours to work this week</Eyebrow>
                <span className="bk-tnum" style={{ fontSize: 20, fontWeight: 900, color: 'var(--text)' }}>{hours}h</span>
              </div>
              <input
                type="range"
                min={0}
                max={Math.max(1, hourCap)}
                step={1}
                value={Math.min(hours, hourCap)}
                onChange={(e) => commitHours(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--brand)' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
                <Chip tone={energy >= 50 ? 'up' : 'down'}>⚡ Energy lets you do {maxHours}h</Chip>
                <Chip tone="neutral">Each hour costs {ENERGY_PER_HOUR} energy</Chip>
              </div>
              {maxHours === 0 && (
                <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--down)', lineHeight: 1.5 }}>
                  ⚠️ You're too worn out to work. Take a break to get your energy back.
                </p>
              )}
            </Card>

            {/* This week preview */}
            <Card style={{ marginBottom: 14 }}>
              <Eyebrow color="var(--up)">This week</Eyebrow>
              <div style={{ display: 'grid', gap: 2, marginTop: 10 }}>
                <Row label="Hours you'll actually work" hint="smaller of: planned, wanted, energy" value={`${result.hoursWorked}h`} />
                <Row label="Energy it costs" value={`−${result.energyCost}`} />
                <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>Revenue at payday</span>
                  <Money value={result.revenue} size={22} />
                </div>
              </div>
            </Card>

            {/* The S-quadrant ceiling lesson */}
            <Card>
              <Eyebrow color="var(--warn)">The ceiling</Eyebrow>
              <div style={{ marginTop: 10 }}>
                <Bar frac={maxHours === 0 ? 0 : Math.min(1, result.hoursWorked / Math.max(1, Math.min(demandHours, maxHours)))} color="var(--warn)" />
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
                💡 Selling your time is the fastest cash — but it has a <b style={{ color: 'var(--text)' }}>hard ceiling</b>.
                There are only so many hours in a week, and when you stop working, the money stops. To break the
                ceiling you need things that earn <i>without</i> you — assets and a business that runs on its own.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, hint, value }: { label: string; hint?: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', gap: 12 }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
        {hint && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)' }}>{hint}</span>}
      </span>
      <span className="bk-tnum" style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{value}</span>
    </div>
  );
}
